import { useConfigSync } from '@/config/useConfig'
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import { getQueryParameter } from '@/services/getQueryParameter'

type RetryMeta = {
  retryCount: number
  usedFailover: boolean
  originalBaseURL: string
  totalRetryCount: number
}

/*
  Default configuration for the client.
*/
const MAX_RETRIES_PER_HOST = 3
const MAX_TOTAL_RETRIES = 6 // 3 tries per host
const BACKOFF_MS = 250
const MAX_BACKOFF_MS = 3000
const DEFAULT_TIMEOUT = 30000

/**
 * Delays the request for the client.
 */
const delay = (attempt: number) => {
  const ms = Math.min(BACKOFF_MS * Math.pow(2, attempt), MAX_BACKOFF_MS)
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Checks if the error is retriable.
 * 5xx errors are retriable.
 */
const isRetriable = (error: AxiosError) => {
  if (!error.response) {
    return true
  }

  const status = error.response.status
  return status >= 500 && status < 600
}

/**
 * Clones the config for the client.
 */
const cloneConfig = (config: AxiosRequestConfig, addFailoverHeader = false): AxiosRequestConfig => {
  const { headers, ...rest } = config
  return {
    ...rest,
    headers: headers ? { ...headers, ...(addFailoverHeader ? { 'x-failover': 'true' } : {}) } : undefined
  }
}

/**
 * Creates a client for the API.
 */
const getClient = (): AxiosInstance => {
  const config = useConfigSync()
  const baseURL = `${config?.api?.url}${config?.api?.service_path}`

  const isOidcFlow = !!(
    getQueryParameter('response_type') &&
    getQueryParameter('client_id') &&
    getQueryParameter('redirect_uri') &&
    getQueryParameter('scope')
  )

  const client: AxiosInstance = axios.create({
    baseURL,
    timeout: DEFAULT_TIMEOUT,
    headers: {
      'content-type': 'application/json',
      'x-app-id': 'LMS',
      ...(isOidcFlow ? { 'x-oidc-flow': 'true' } : {})
    }
  })

  /**
   * Interceptor that implements the retry logic.
   * The idea is to retry the request on the same host if the request fails up to MAX_RETRIES_PER_HOST times.
   * If the request fails after MAX_RETRIES_PER_HOST times, we switch to the failover host.
   * If the request fails after MAX_TOTAL_RETRIES times, we give up.
   *
   * Canary endpoints are not retried.
   */
  client.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      // Get the config for the request and the retry meta
      const cfg = error.config as AxiosRequestConfig & { _retryMeta?: RetryMeta }

      // If the config is not found, throw the error
      if (!cfg) throw error

      // Initialize the retry meta if it is not found
      cfg._retryMeta = cfg._retryMeta ?? {
        retryCount: 0,
        usedFailover: false,
        originalBaseURL: baseURL,
        totalRetryCount: 0
      }

      // If the error is not retriable, throw the error
      if (!isRetriable(error)) throw error

      // Get the retry meta
      const meta = cfg._retryMeta

      // Check total retry limit to prevent infinite loops, if we have reached the limit, throw the error
      if (meta.totalRetryCount >= MAX_TOTAL_RETRIES) {
        throw error
      }

      // Try retrying on the same host first
      if (meta.retryCount < MAX_RETRIES_PER_HOST) {
        const attempt = meta.retryCount
        meta.retryCount = attempt + 1
        meta.totalRetryCount = meta.totalRetryCount + 1

        await delay(attempt)

        const retryCfg = cloneConfig(cfg)
        return client.request(retryCfg)
      }

      // Try failover host if we haven't used it yet
      if (!meta.usedFailover) {
        meta.usedFailover = true
        meta.retryCount = 0
        meta.totalRetryCount = meta.totalRetryCount + 1

        // Update the baseURL and URL to use failover
        if (cfg.url) {
          // We are using the full path otherwise we would apply the same logic to canary endpoints, which we don't want
          const base = `${config?.api?.url}${config?.api?.service_path}`
          const failover = `${config?.api?.url}${config?.api?.failover_service_path}`

          cfg.baseURL = baseURL.replace(base, failover)
          cfg.url = cfg.url.replace(base, failover)
        }

        const retryCfg = cloneConfig(cfg, true)
        return client.request(retryCfg)
      }

      // If we've used failover and exhausted retries, give up
      throw error
    }
  )

  return client
}

export { getClient }
